'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useLocale, useLocalizedPath } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

export default function ContactSection() {
  const locale = useLocale();
  const localizePath = useLocalizedPath();
  const copy = getPublicStrings(locale).home.contact;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: copy.services[0],
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [emailError, setEmailError] = useState('');

  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitted(false);

    const email = formData.email.trim().toLowerCase();
    if (!validateEmail(email)) {
      setEmailError(copy.validEmail);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          email,
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          message: formData.message.trim(),
        }),
      });

      const result = (await response.json()) as { success: boolean; error?: string };

      if (!response.ok || !result.success) {
        setSubmitError(result.error || copy.submitErrorFallback);
        return;
      }

      setFormData({
        name: '',
        email: '',
        phone: '',
        service: copy.services[0],
        message: '',
      });
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 6000);
    } catch (_error) {
      setSubmitError(copy.networkError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    if (e.target.name === 'email') {
      const nextEmail = e.target.value;
      setEmailError(nextEmail.length > 0 && !validateEmail(nextEmail) ? copy.validEmail : '');
    }
    if (submitError) {
      setSubmitError('');
    }
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const serviceOptions = copy.services;

  return (
    <section id="contact" className="py-16 md:py-32 px-6 md:px-12 bg-foreground">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block py-2 px-4 border border-gold text-[10px] tracking-[0.3em] uppercase text-gold font-black mb-6">
            {copy.eyebrow}
          </span>
          <h2 className="text-4xl md:text-6xl text-white leading-tight font-heading font-extrabold mb-6">
            {copy.titleBefore} <span className="text-gold italic">{copy.titleItalic}</span>
          </h2>
          <div className="space-y-4 max-w-3xl mx-auto">
            <p className="text-lg text-white/75 font-light leading-relaxed">{copy.p1}</p>
            <p className="text-lg text-white/75 font-light leading-relaxed">{copy.p2}</p>
          </div>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} noValidate className="bg-white p-8 md:p-12 space-y-8">
          {/* Success Message */}
          {isSubmitted && (
            <div className="flex items-center gap-3 p-4 bg-success/10 border border-success/30 text-success">
              <Icon name="CheckCircleIcon" size={20} />
              <p className="text-sm font-bold">{copy.success}</p>
            </div>
          )}
          {submitError && (
            <div className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/30 text-destructive">
              <Icon name="ExclamationCircleIcon" size={20} />
              <p className="text-sm font-bold">{submitError}</p>
            </div>
          )}

          {/* Name & Email Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="name"
                className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3"
              >
                {copy.fullName}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full bg-transparent border-b-2 border-border py-3 text-foreground focus:outline-none focus:border-gold transition-colors"
                placeholder={copy.fullNamePlaceholder}
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3"
              >
                {copy.emailAddress}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-transparent border-b-2 border-border py-3 text-foreground focus:outline-none focus:border-gold transition-colors"
                placeholder={copy.emailPlaceholder}
                aria-invalid={Boolean(emailError)}
                aria-describedby="email-error"
              />
              {emailError && (
                <p id="email-error" className="text-xs text-destructive mt-2">
                  {emailError}
                </p>
              )}
            </div>
          </div>

          {/* Phone & Service Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="phone"
                className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3"
              >
                {copy.phoneOptional}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-transparent border-b-2 border-border py-3 text-foreground focus:outline-none focus:border-gold transition-colors"
                placeholder="+31 6 5896 9744"
              />
            </div>
            <div>
              <label
                htmlFor="service"
                className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3"
              >
                {copy.serviceInterest}
              </label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                aria-label={copy.serviceAria}
                className="w-full bg-transparent border-b-2 border-border py-3 text-foreground focus:outline-none focus:border-gold transition-colors appearance-none cursor-pointer"
              >
                {serviceOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Message */}
          <div>
            <label
              htmlFor="message"
              className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3"
            >
              {copy.message}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={5}
              className="w-full bg-transparent border-2 border-border p-4 text-foreground focus:outline-none focus:border-gold transition-colors resize-none"
              placeholder={copy.messagePlaceholder}
            />
          </div>

          {/* Submit Button */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground max-w-md">
              {copy.privacyAgreementPrefix}{' '}
              <Link
                href={localizePath('/privacy')}
                className="text-primary underline underline-offset-2"
              >
                {copy.privacyPolicy}
              </Link>
              . {copy.privacyAgreementSuffix}
            </p>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full md:w-auto px-12 py-4 bg-gold hover:bg-gold/90 text-foreground text-sm font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 group shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2"
            >
              {isSubmitting ? copy.sending : copy.sendMessage}
              <Icon
                name="PaperAirplaneIcon"
                size={18}
                className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
              />
            </button>
          </div>
        </form>

        {/* Contact Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 text-center">
          <div>
            <Icon name="EnvelopeIcon" size={24} className="text-gold mx-auto mb-4" />
            <div className="text-sm text-white/60 mb-1">{copy.infoEmail}</div>
            <a
              href="mailto:info@academyofsquash.com"
              className="text-white hover:text-gold transition-colors"
            >
              info@academyofsquash.com
            </a>
          </div>
          <div>
            <Icon name="PhoneIcon" size={24} className="text-gold mx-auto mb-4" />
            <div className="text-sm text-white/60 mb-1">{copy.infoPhone}</div>
            <a href="tel:+31658969744" className="text-white hover:text-gold transition-colors">
              +31 6 5896 9744
            </a>
          </div>
          <div>
            <Icon name="MapPinIcon" size={24} className="text-gold mx-auto mb-4" />
            <div className="text-sm text-white/60 mb-1">{copy.infoLocation}</div>
            <div className="text-white">{copy.location}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
